import { useEffect, useState, useCallback } from "react";
import { Box, Flex, Stack, Text, HStack, Badge, Icon, Spinner } from "@chakra-ui/react";
import { Megaphone } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { colors } from "@/theme/colors";
import { logWarn } from "@/lib/log";

// Home-dashboard card surfacing the latest Safety Bulletins (Hakina global
// posts + the company's own). Gated by the two-tier safety_bulletins feature;
// updates live when Hakina publishes a new bulletin.
export const SafetyBulletinsCard = () => {
  const { hasFeature, activeCompanyId } = useAuth();
  const show = hasFeature("safety_bulletins");
  const [items, setItems] = useState(null);
  const [opening, setOpening] = useState(null);

  const load = useCallback(async () => {
    let q = supabase.from("resources")
      .select("id,title,description,created_at,company_id,file_path")
      .eq("category", "Safety Bulletins")
      .order("created_at", { ascending: false })
      .limit(3);
    q = activeCompanyId ? q.or(`company_id.is.null,company_id.eq.${activeCompanyId}`) : q.is("company_id", null);
    const { data } = await q;
    setItems(data || []);
  }, [activeCompanyId]);

  useEffect(() => { if (show) load(); }, [show, load]);

  useEffect(() => {
    if (!show) return;
    let chans = [];
    (async () => {
      try {
        await supabase.realtime.setAuth();
        const topics = ["bulletins:global", ...(activeCompanyId ? [`bulletins:${activeCompanyId}`] : [])];
        chans = topics.map((t) =>
          supabase.channel(t, { config: { private: true } })
            .on("broadcast", { event: "bulletin" }, load)
            .subscribe()
        );
      } catch (e) { logWarn("[bulletins] realtime subscribe failed:", e); }
    })();
    return () => chans.forEach((c) => supabase.removeChannel(c));
  }, [show, activeCompanyId, load]);

  const open = async (r) => {
    setOpening(r.id);
    const { data, error } = await supabase.storage.from("resources").createSignedUrl(r.file_path, 300);
    setOpening(null);
    if (!error && data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener");
  };

  if (!show) return null;

  return (
    <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="4" data-testid="bulletins-card">
      <HStack gap="2" mb="3">
        <Flex align="center" justify="center" w="30px" h="30px" borderRadius="8px" bg={colors.tealTint}>
          <Icon as={Megaphone} boxSize="4" color={colors.teal} />
        </Flex>
        <Text fontWeight="700" fontSize="sm" color={colors.textPrimary}>Safety Bulletins</Text>
      </HStack>

      {items === null ? (
        <Flex justify="center" py="4"><Spinner size="sm" color={colors.teal} /></Flex>
      ) : items.length === 0 ? (
        <Text fontSize="sm" color={colors.textSecondary} py="2" data-testid="bulletins-empty">
          No bulletins yet — Hakina's monthly safety blogs and tool talks will appear here.
        </Text>
      ) : (
        <Stack gap="2.5">
          {items.map((r) => (
            <Box key={r.id} as="button" type="button" textAlign="left" onClick={() => open(r)}
              opacity={opening === r.id ? 0.6 : 1}
              borderRadius="10px" p="2.5" bg={colors.cream} border="1px solid" borderColor={colors.border}
              transition="background-color 0.15s ease" _hover={{ bg: colors.tealTint }}
              data-testid={`bulletin-item-${r.id}`}>
              <HStack gap="2" flexWrap="wrap">
                <Text fontWeight="600" fontSize="sm" color={colors.textPrimary} lineClamp={1}>{r.title}</Text>
                {r.company_id === null && (
                  <Badge bg={colors.teal} color="white" borderRadius="full" px="2" fontSize="9px" data-testid={`hakina-badge-${r.id}`}>
                    Hakina
                  </Badge>
                )}
              </HStack>
              <Text fontSize="11px" color={colors.textSecondary} mt="0.5">
                {new Date(r.created_at).toLocaleDateString([], { dateStyle: "medium" })}
              </Text>
              {r.description && (
                <Text fontSize="xs" color={colors.textSecondary} mt="1" lineClamp={2}>{r.description}</Text>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};
